import React, { useState, useEffect } from "react";
import { Modal, Button, Badge, Dropdown, Col } from "react-bootstrap";
import {
  IconCalendar,
  IconClock,
  IconEdit,
  IconTrash,
  IconX,
  IconDots,
  IconFileText,
  IconShare,
  IconStethoscope,
  IconCurrencyReal,
  IconCircleFilled,
  IconSelect,
  IconUserQuestion,
} from "@tabler/icons-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import "./eventDetailsModal.css";
import { formatCurrency, formatDescriptionHybrid } from "./utils";
import NumberFormat from "react-number-format";

// Status de confirmação dos eventos
const CONFIRMATION_STATUS = {
  pending: { label: "Pendente", color: "#ff9900" },
  canceled: { label: "Cancelado", color: "#dc3545" },
  confirmed: { label: "Confirmado", color: "#28a745" },
};

const EventDetailsModal = ({
  show,
  onHide,
  event,
  onEdit,
  onDelete,
  calendars = [],
  appointmentTypes = [],
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  if (!event) return null;

  const eventStart = new Date(event.start);
  const eventEnd = new Date(event.end);
  const calendarInfo = calendars.find(
    (cal) => cal.id === event.extendedProps?.calendar
  );
  const appointmentType = event.extendedProps?.appointment_type;
  const appointmentTypeInfo = appointmentTypes.find(
    (type) => type.id === appointmentType?.id
  );

  // Status de confirmação
  
  const confirmationStatus = event.extendedProps?.confirmation || "pending";
  const statusInfo =
    CONFIRMATION_STATUS[confirmationStatus] || CONFIRMATION_STATUS.pending;
  const formatEventDate = (startDate, endDate) => {
    const isSameDay =
      format(startDate, "yyyy-MM-dd") === format(endDate, "yyyy-MM-dd");

    if (isSameDay) {
      return {
        date: format(startDate, "EEEE, dd 'de' MMMM 'de' yyyy", {
          locale: ptBR,
        }),
        time: `${format(startDate, "HH:mm")} - ${format(endDate, "HH:mm")}`,
      };
    } else {
      return {
        date: `${format(startDate, "dd/MM/yyyy")} - ${format(
          endDate,
          "dd/MM/yyyy"
        )}`,
        time: `${format(startDate, "HH:mm")} - ${format(endDate, "HH:mm")}`,
      };
    }
  };

  const { date, time } = formatEventDate(eventStart, eventEnd);

  const handleEdit = () => {
    setShowDropdown(false);
    onEdit();
  };

  const handleDelete = () => {
    setShowDropdown(false);
    onDelete();
  };


  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        centered
        className={`event-details-modal ${isMobile ? "mobile" : "desktop"}`}
        size={isMobile ? "sm" : "md"}
      >
        {/* Header customizado */}
        <div className="event-modal-header">
          <div className="event-header-content">
            <div
              className="event-color-indicator"
              style={{ backgroundColor: "var(--primary)" }}
            />
            <div className="event-header-text">
              <h4 className="event-title">{event.title}</h4>
              <div className="event-header-badges">
                {calendarInfo && (
                  <div className="event-calendar-info">
                    <IconCalendar size={14} />
                    <span>{calendarInfo.name}</span>
                    {calendarInfo.is_shared && (
                      <Badge bg="secondary" className="ms-1">
                        <IconShare size={12} className="me-1" />
                        Compartilhado
                      </Badge>
                    )}
                  </div>
                )}

                {/* Badge de status de confirmação */}
                <span
                  style={{
                    backgroundColor: statusInfo.color,
                    color: "#fff",
                  }}
                  className="badge ms-1"
                >
                  <IconCircleFilled size={10} className="me-1" />
                  {statusInfo.label}
                </span>
              </div>
            </div>
          </div>

          <div className="event-header-actions">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={onHide}
              className="close-btn"
            >
              <IconX size={16} />
            </Button>
          </div>
        </div>

        <Modal.Body className="event-modal-body">
          {/* Informações de data e hora */}
          <div className="event-info-section">
            <div className="event-info-item">
              <div className="event-info-icon">
                <IconCalendar size={20} color={"var(--primary)"} />
              </div>
              <div className="event-info-content">
                <div className="event-info-label">Data</div>
                <div className="event-info-value">{date}</div>
              </div>
            </div>

            <div className="event-info-item">
              <div className="event-info-icon">
                <IconClock size={20} color={"var(--primary)"} />
              </div>
              <div className="event-info-content">
                <div className="event-info-label">Horário</div>
                <div className="event-info-value">{time}</div>
              </div>
            </div>
          </div>

          {/* Informações do tipo de atendimento */}
          {appointmentTypeInfo && (
            <div className="event-info-section">
              <div className="event-info-item">
                <div className="event-info-icon">
                  <IconSelect size={20} color={"var(--primary)"} />
                </div>
                <div className="event-info-content">
                  <div className="event-info-label">Tipo de Atendimento</div>
                  <div className="event-info-value">
                    <div className="appointment-type-info">
                      <span className="appointment-type-name">
                        {appointmentTypeInfo.name}
                      </span>
                      {appointmentTypeInfo.duration && (
                        <Badge bg="light" text="secondary" className="ms-2">
                          {appointmentTypeInfo.duration} min
                        </Badge>
                      )}
                    </div>
                    {appointmentTypeInfo.description && (
                      <div className="appointment-type-description text-muted mt-1">
                        {appointmentTypeInfo.description}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Informações de preço */}
          {event.extendedProps?.price && (
            <div className="event-info-section">
              <div className="event-info-item">
                <div className="event-info-icon">
                  <IconCurrencyReal size={20} color={"var(--primary)"} />
                </div>
                <div className="event-info-content">
                  <div className="event-info-label">Preço</div>
                  <div className="event-info-value">
                    {
                      <NumberFormat
                        displayType={"text"}
                        value={event.extendedProps.price}
                        fixedDecimalScale={true}
                        decimalScale={2}
                        thousandSeparator="."
                        decimalSeparator=","
                        prefix="R$"
                      />
                    }
                  </div>
                </div>
              </div>
            </div>
          )}
          {event.extendedProps?.created_by && (
            <div className="event-info-section">
              <div className="event-info-item">
                <div className="event-info-icon">
                  <IconUserQuestion size={20} color={"var(--primary)"} />
                </div>
                <div className="event-info-content">
                  <div className="event-info-label">Responsável</div>
                  <div className="event-info-value">
                    {event.extendedProps?.created_by}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Descrição */}
          {event.extendedProps?.description && (
            <div className="event-description-section">
              <div className="event-description-header">
                <IconFileText size={18} color={"var(--primary)"} />
                <span>Descrição</span>
              </div>
              <div className="event-description-content">
                {formatDescriptionHybrid(event.extendedProps.description)}
              </div>
            </div>
          )}

          {/* Ações para mobile na parte inferior */}
          {isMobile && (
            <div className="mobile-actions">
              <Button
                variant="outline-primary"
                className="mobile-action-btn"
                onClick={handleEdit}
              >
                <IconEdit size={18} className="me-2" />
                Editar Agendamento
              </Button>
              <Button
                variant="outline-danger"
                className="mobile-action-btn"
                onClick={handleDelete}
              >
                <IconTrash size={18} className="me-2" />
                Cancelar Agendamento
              </Button>
            </div>
          )}
          {/* Desktop - Botões separados */}
          {!isMobile && (
            <div className="text-right">
              <hr></hr>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleEdit}
                className="me-2"
              >
                <IconEdit size={16} /> Editar
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleDelete}
                className="me-2"
              >
                <IconTrash size={16} /> Cancelar
              </Button>
            </div>
          )}
        </Modal.Body>

        {/* Footer apenas para desktop */}
        {!isMobile && (
          <Modal.Footer className="event-modal-footer">
            <Button variant="secondary" onClick={onHide}>
              Fechar
            </Button>
          </Modal.Footer>
        )}
      </Modal>
    </>
  );
};

export default EventDetailsModal;
